import * as React from "react";
import { GridCellKind } from "@glideapps/glide-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { checkReference } from './utils'
import { saveRow, saveCell, runOnSelected, stopRunCell } from './api'
import { getCellKind } from '../../pages/components/cells/aicellUtils'
import _ from 'lodash'
import moment from 'moment'
import { dispatch } from "store";

let headerButtonBounds = {};
const getHeaderTextWidth = (text) => {
    return (text.length * 10) + 85
};
export function getDuplicateRowIndex(dupRows, rows) {
    const indexes = []
    rows.map((row, i) => {
        if (dupRows.includes(row._id)) {
            indexes.push(i)
        }
    })
    return indexes
}
const getLocalTime = (message) => {
    if (message && typeof message === 'string' && message.includes('Will Retry at ')) {
        try {
            const retryTime = message.split('Will Retry at ')[1]
            const localTime = moment(retryTime).local().format('HH:mm:ss A')
            message = `Will Retry after ${localTime}`
        } catch (e) {
            console.log('Error in parsing retry time', e)
        }
    }
    return message
}
export const errorInValue = (value) => {
    //Error: MCP tool
    if(value && typeof value === 'object' && value.error) {
        return true
    } else if (value && typeof value === 'string') {
        value = value.toLowerCase()
        if(value.includes('error: mcp tool')) return true
        if(value.includes('error: error')) return true
        if(value.includes('error:')) return true
        if(value.includes('__error__')) return true
        if(value.includes('errs_')) return true
        if(value.includes('err_')) return true
    }
}
export function useDataGetter(clientWidth) {
    const dispatch = useDispatch();
    const { headings, filteredRows, filteredRowsCount, height, table } = useSelector(state => state.datagrid);
    const { currentSheetData } = useSelector(state => state.sheets);
    const { cols, AICols, pin, Conditional } = React.useMemo(() => {
        const AICols = []
        const Conditional = []
        const hiddenCols = headings.filter(a => !a.hidden)
        let totalWidth = 120
        const cols = hiddenCols.map((heading, i) => {
            const isLast = i === hiddenCols.length - 1
            if (heading.type === 'AI' || heading.type === 'Waterfall') {
                AICols.push(heading._id)
            }
            if ((heading.condition !== undefined &&  heading.isConditional && heading.type==='AI')) {
                Conditional.push(heading._id)
            }
            const headerIcon =Conditional.includes(heading._id) ? 'condition' : heading.type === 'AI' ? 'ai_mk' : heading.type === 'Formula' ? 'formulaIcon' : 'text_mk'
            const w = heading.width ?? 200
            const labelWidth = getHeaderTextWidth(heading.label)
            const width = w > labelWidth ? w : labelWidth
            if (isLast) {
                totalWidth += width
                if (totalWidth < clientWidth) {
                    const diff = clientWidth - totalWidth
                    return ({
                        id: heading._id,
                        title: heading.label,
                        width: width + diff,
                        icon: headerIcon,
                        menuIcon: "triangle",
                        hasMenu: true
                    })
                }
            }
            totalWidth += width
            if (heading.title === 'row_id') {
                return ({
                    id: heading._id,
                    title: heading.label,
                    width: 230,
                    hasMenu: false
                })
            }
            return ({
                id: heading._id,
                title: heading.label,
                width,
                icon: headerIcon,
                menuIcon: "triangle",
                hasMenu: true
            })
        });
        return { cols, AICols, pin: table.pin, Conditional }
    }, [headings, clientWidth, table]);

    const getCellContent = React.useCallback(([col, row]) => {
        const allowWrapping = height > 40
        const dataRow = filteredRows[row];
        if(!dataRow) {
            return {
                kind: GridCellKind.Text,
                allowOverlay: false,
                displayData: '',
                data: '',
                readOnly: true,
            };
        }
        const cellId = cols[col]?.id;
        const cell = headings.find(h => h._id === cellId);
        if (cell && cell.title === 'row_id') {
            return {
                kind: GridCellKind.RowID,
                allowOverlay: false,
                displayData: dataRow?._id,
                data: dataRow?._id,
                readOnly: true,
            };
        }

        const datatype = cell?.datatype;
        const originalValue = dataRow?.[cellId];
        let value = dataRow?.[cellId] ?? '';
        const options = cell?.optionsArr;
        const refData = {
            rowId: dataRow?._id,
            cellId: cellId,
            rowIndex: row,
            colIndex: col,
            value: value,
            datatype: cell?.datatype,
            originalValue: originalValue,
            image: { logo_url: dataRow.logo_url ?? dataRow.organization?.logo_url, photo_url: dataRow.photo_url, cell }
        }
        const isAi = AICols.includes(cellId);
        if (isAi) {
            if (value) {
                value = getLocalTime(value)
            }
            let hasError = false;
            let isNotFound = false;
            const hasPrompt = cell.prompt ? cell.prompt.trim() !== '' : false
            if (!hasPrompt && !value) {
                return {
                    kind: GridCellKind.Text,
                    allowOverlay: true,
                    displayData: 'No prompt found',
                    data: '',
                    copyData: '',
                    readOnly: true,
                    themeOverrides: {
                        backgroundColor: 'rgba(255, 0, 0, 0.1)',
                        color: 'rgba(255, 0, 0, 0.8)',
                    },
                    allowWrapping,
                };
            }
            let conditionPassed = dataRow[`__cal_${cellId}`] ?? true
            if (cell.isConditional && !conditionPassed) {
                return {
                    kind: GridCellKind.Custom,
                    allowOverlay: true,
                    displayData: value ? value.toString() : 'Condition not met',
                    copyData: value ? value.toString() : '',
                    data: {
                        ...refData,
                        isConditional: true,
                        kind: "error-cell",
                        error: 'Not Found',
                        conditionPassed: false,
                    },
                    readOnly: true,
                    allowWrapping,
                };
            }
            if (value && typeof value === 'string' && value.toLowerCase() === 'not found') {
                return {
                    kind: GridCellKind.Custom,
                    allowOverlay: true,
                    copyData: value ? value.toString() : '',
                    data: {
                        ...refData,
                        kind: "error-cell",
                        error: 'Not Found',
                        fixable: false,
                        hasError: true,
                        isNotFound: true,
                        hasPrompt: true
                    },
                    readOnly: true,
                    allowWrapping,
                }
            }
            if (value && typeof value === 'object' && value.hasOwnProperty('error') && value.hasOwnProperty('status')) {
                hasError = value.error === 'Error'
                value = 'Error'
            } else if (value && typeof value === 'object' && _.isArray(value)) {
                // value = value
            } else if (value && typeof value === 'object' && !_.isDate(value) && cell?.datatype !== 'json') {
                value = 'Object'
            }
            let isLoading = value === 'Adding to queue...' || value === 'Processing...' || value === 'InQueue' || value === 'Warming up system...'
            if (errorInValue(value)) {
                value = value.replaceAll('___Error__', '').replaceAll('ERRS_', '').replaceAll('ERR_', '').replaceAll('Error: ', '').replaceAll('Error: MCP tool', '')
                return {
                    kind: GridCellKind.Custom,
                    allowOverlay: true,
                    copyData: value ? value.toString() : '',
                    data: {
                        ...refData,
                        kind: "error-cell",
                        error: value,
                        fixable: value.toLowerCase().includes('connection not found'),
                        hasError: true,
                        isNotFound: false,
                        hasPrompt: true
                    },
                    readOnly: true,
                    allowWrapping,

                }
            }
            if ((value && typeof value === 'string' && value.includes('__lk__')) || isLoading) {
                value = value.replaceAll('__lk__', '')
                isLoading = true
            }
            if (isLoading) {
                const d = {
                    kind: GridCellKind.Custom,
                    allowOverlay: true,
                    copyData: value ? value.toString() : '',
                    data: {
                        ...refData,
                        kind: "processing-cell",
                        text: value,
                    },
                    readOnly: true,
                };
                return d;
            }
            let ErrorMsg = 'Error'
            let fixable = false
            
            if (!isLoading && value && typeof value === 'string') {
                hasError = value && value != null ? value.trim().toLowerCase() === 'error' : false
                if (errorInValue(value)) {
                    hasError = true
                    ErrorMsg = value.replace('___Error__', '').replace('ERRS_', '').replace('ERR_', '')
                    fixable = value.toLowerCase().includes('connection not found')
                }
                if (hasError) {
                    const d = {
                        kind: GridCellKind.Custom,
                        allowOverlay: true,
                        copyData: value ? value.toString() :  '',
                        data: {
                            ...refData,
                            kind: "error-cell",
                            error: ErrorMsg,
                            fixable: fixable,
                            hasError: hasError,
                            isNotFound: isNotFound,
                            hasPrompt: hasPrompt
                        },
                        readOnly: true,
                        allowWrapping,

                    };
                    return d;
                }
            }
            const emptyRef = checkReference(dataRow, headings, cell.prompt ?? '')
            if (emptyRef && emptyRef.length > 0 && !value) {
                const t = emptyRef.map((ref) => `Missing input ${ref}`).join(', ')
                return {
                    kind: GridCellKind.Custom,
                    allowOverlay: true,
                    copyData: value ? value.toString() : '',
                    displayData: t,
                    data: {
                        ...refData,
                        kind: "ai-cell",
                        value: t,
                        emptyRef
                    },
                    readOnly: true,
                    allowWrapping,
                };
            }
            const hasReferences = emptyRef.length === 0 || !emptyRef
            if (!hasReferences && !value) {
                return {
                    kind: GridCellKind.Text,
                    allowOverlay: true,
                    copyData: value ? value.toString() : '',
                    displayData: 'Please fill all the references',
                    data: {
                        ...refData,
                        emptyRef
                    },
                    readOnly: true,
                    allowWrapping,
                };
            }
            
            if (cell && cell.datatype === 'imageurl') {
                const isValidUrl = value && value != null ? value.trim().toLowerCase().startsWith('http') : false
                // const testImageUrl = 'https://meerkats.ai/images/home1.png'
                return {
                    kind: GridCellKind.Custom,
                    allowOverlay: true,
                    displayData: isValidUrl ? [value] : [],
                    copyData: value ? value.toString() : '',
                    data: {
                        ...refData,
                        kind: "ai-cell",
                        value: isValidUrl ? [value] : [],
                        options: options,
                    },
                    readOnly: false,
                    allowWrapping
                };

            } if (cell && cell.datatype === 'json') {
                return {
                    kind: GridCellKind.Custom,
                    allowOverlay: true,
                    displayData: value,
                    copyData: value ? JSON.stringify(value) : '',
                    rowId: dataRow._id,
                    data: {
                        ...refData,
                        kind: "ai-cell",
                        value: value,
                        rowId: dataRow._id,
                    },
                    readOnly: false,
                    allowWrapping
                };

            }
            if (cell && cell.datatype === 'url' && cell.childTable) {
                return {
                    kind: GridCellKind.Custom,
                    allowOverlay: true,
                    displayData: value,
                    copyData: value ? value.toString() : '',
                    data: {
                        ...refData,
                        kind: "ai-cell",
                        value: `${window.origin}/dashboard/table/${cell.childTable}/${value}`,
                        options: options,
                    },
                    readOnly: false,
                    allowWrapping
                };
            }
            return {
                kind: GridCellKind.Custom,
                allowOverlay: true,
                displayData: value,
                copyData: value ? value.toString() : '',
                data: {
                    ...refData,
                    kind: "ai-cell",
                    value: value,
                    options: options,
                },
                readOnly: false,
                allowWrapping
            };
        }
        const cellKind = getCellKind(datatype, value, options, refData.originalValue, refData.image)
        cellKind.allowWrapping = allowWrapping
        return cellKind
    }, [filteredRows, cols, headings, AICols, height]);

    const setCellValue = React.useCallback(([col, row], newValue) => {
        if (newValue.readOnly) {
            return
        }
        let value = undefined
        let options = undefined
        const data = newValue
        if (data && data.kind === GridCellKind.Custom) {
            if (data.data.kind === "dropdown-cell") {
                options = data.data.allowedValues
                value = data.data.value
            } else if (data.data.kind === "multi-select-cell") {
                options = data.data.options
                value = data.data.values
            } else if (data.data.kind === "date-picker-cell" && data.data.format === 'date') {
                value = data.data.date
            } else if (data.data.kind === "date-picker-cell" && data.data.format === 'time') {
                value = data.data.date
            } else if (data.data.kind === "date-picker-cell" && data.data.format === 'datetime-local') {
                value = data.data.date
            } else {
                value = data.data
            }
        } else if (typeof newValue !== 'object') {
            return
        } else {
            value = newValue.data
        }
        const rowId = filteredRows[row]?._id;
        const cell = cols[col];
        const cellId = cell.id;
        if (options && options.length > 0) {
            const heading = headings.find(h => h._id === cellId);
            if (heading && heading.optionsArr && heading.optionsArr.length !== options.length) {
                dispatch({
                    type: 'UPDATE_HEADING_DG',
                    payload: {
                        ...heading,
                        optionsArr: options,
                    },
                });
                saveCell({ ...heading, optionsArr: options })
            }
        }


        dispatch({
            type: 'UPDATE_CELL_DATA_DG',
            payload: {
                rowId,
                cellId,
                value,
            }
        });

        saveRow({ isTable: true, cellId, value, rowId });
    }, [dispatch, filteredRows, cols, headings]);

    const onColumnResize = React.useCallback((column, newSize) => {
        const heading = headings.find(h => h._id === column.id);
        const labelWidth = getHeaderTextWidth(heading.label)
        if (heading) {
            dispatch({
                type: 'UPDATE_HEADING_DG',
                payload: {
                    ...heading,
                    width: newSize > labelWidth ? newSize : labelWidth,
                },
            });
        }
    }, [dispatch, headings]);
    
    return {
        cols,
        getCellContent,
        onColumnResize,
        setCellValue,
        rows: filteredRowsCount,
        AICols,
        pin,
        headerButtonBounds,
        totalRows: currentSheetData?.totalRows,
        Conditional
    };
}



export const defaultProps = {
    smoothScrollX: true,
    smoothScrollY: true,
    getCellsForSelection: true,
    width: "100%",
    maxColumnAutoWidth: 500,
    maxColumnWidth: 2000,
    scaleToRem: true,
    freezeColumns: 1,
    rowMarkers: "both",
    isDraggable: true,
    trailingRowOptions: {
        hint: "New row...",
        sticky: true,
        tint: true
    },
    rightElementProps: {
        fill: false,
        sticky: false
    }
};

export function clearCell(cell) {
    switch (cell.kind) {
        case GridCellKind.Boolean:
            return {
                ...cell,
                data: false,
            };
        case GridCellKind.Image:
            return {
                ...cell,
                data: [],
                displayData: [],
            };
        case GridCellKind.Uri:
        case GridCellKind.Markdown:
            return {
                ...cell,
                data: "",
            };
        case GridCellKind.Text:
            return {
                ...cell,
                data: "",
                displayData: "",
            };
        case GridCellKind.Number:
            return {
                ...cell,
                data: 0,
                displayData: "",
            };
        default:
            return cell;
    }
}
export const isOverIcon = (posX, posY, iconX, iconY, iconDiameter, bounds, type) => {
    iconX = iconX - bounds.x;
    iconY = iconY - bounds.y;
    const mouseX = posX;
    const mouseY = posY;
    const iconCenterX = iconX + iconDiameter / 2;
    const iconCenterY = iconY + iconDiameter / 2;
    const dx = mouseX - iconCenterX;
    const dy = mouseY - iconCenterY;
    const distanceSquared = dx * dx + dy * dy;
    const radius = iconDiameter / 2;
    const radiusSquared = radius * radius;
    return distanceSquared <= radiusSquared;
};

export const onCellClick = ({ cell, heading, row, cellData }) => {
    if(!cell || !heading || !row || !cellData   ) return
    const kind = cellData?.data?.kind
    const isAI = heading.type === 'AI' || heading.type === 'Waterfall'
    if (!isAI) {
        dispatch({
            type: 'OPEN_CELL_RUN_LOG',
            payload: {
                rowId: row._id,
                cell: heading._id,
                type: 'output',
                data: row[heading._id],
                kind: heading.datatype,
                isAI,
                heading
            }
        });
        return
    } else if (kind === "ai-cell" || kind === "error-cell" || kind === "processing-cell") {
        if (cell.localEventX !== undefined && cell.localEventY !== undefined) {
            const iconSize = 16;
            const cellHorizontalPadding = 8; // Approximate value
            const iconX = cell.bounds.x + cell.bounds.width - iconSize - cellHorizontalPadding;
            const iconY = cell.bounds.y + (cell.bounds.height - iconSize) / 2;
            // Check if click is on the icon (play or stop)
            const isIconClick = isOverIcon(
                cell.localEventX,
                cell.localEventY,
                iconX,
                iconY,
                iconSize,
                cell.bounds
            );
            if (isIconClick) {
                if (kind === "processing-cell") {
                    stopRunCell({
                        rowId: row._id,
                        cellId: heading._id,
                        rowIndex: cell.location[1]
                    });

                } else {
                    runOnSelected([row._id], heading._id, false);
                }
                return;
            } else {
                dispatch({
                    type: 'OPEN_CELL_RUN_LOG',
                    payload: {
                        rowId: row._id,
                        cell: heading._id,
                        type: 'output',
                        data: row[heading._id],
                        kind: heading.datatype,
                        isAI,
                        heading
                    }
                });
            }
        }
    }
}